import React, { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingSpinner from "../components/LoadingSpinner";
import API_URL from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import useRouteTab from "../hooks/useRouteTab";
import apiService from "../services/api";
import proxyApiService from "../services/proxy-api";
import { isReadOnlyPlaylistEntry } from "../utils/playlistDashboard";

const READ_ONLY_PLAYLIST_MESSAGE =
  "This playlist is managed by your reseller and can't be changed here.";

const PLAYLIST_MANAGER_TAB_IDS = [
  "overview",
  "channels",
  "movies",
  "series",
  "favorites",
  "settings",
];

// Fix for React 18 StrictMode
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const PlaylistManager = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // State management
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useRouteTab({
    defaultTab: "overview",
    validTabs: PLAYLIST_MANAGER_TAB_IDS,
  });
  const [domain, setDomain] = useState("");
  const [channelCounts, setChannelCounts] = useState({
    series: 0,
    movies: 0,
    liveTv: 0,
    total: 0,
  });

  // Settings states
  const [settings] = useState({
    clearCacheAuto: false,
    useHardwareDecoder: false,
    closedCaptions: false,
    muteMiniplayer: false,
    startAtLaunch: false,
  });

  // Channels
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryChannels, setCategoryChannels] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryPagination, setCategoryPagination] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const [searchPagination, setSearchPagination] = useState(null);
  const [hiddenCategories, setHiddenCategories] = useState(new Set());
  const [lockedCategories, setLockedCategories] = useState(new Set());
  const [userPinCode, setUserPinCode] = useState("");
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinAction, setPinAction] = useState(""); // 'reset', 'setAndLock'
  const [currentPinCategory, setCurrentPinCategory] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [showHiddenCategories, setShowHiddenCategories] = useState(false);
  const [categoryOrder, setCategoryOrder] = useState([]);

  // Movies
  const [movieCategories, setMovieCategories] = useState([]);
  const [loadingMovieCategories, setLoadingMovieCategories] = useState(false);
  const [movieSearchQuery, setMovieSearchQuery] = useState("");
  const [movieSearchResults, setMovieSearchResults] = useState([]);
  const [movieSearchLoading, setMovieSearchLoading] = useState(false);
  const [movieSearchDebounceTimer, setMovieSearchDebounceTimer] =
    useState(null);
  const [movieSearchPagination, setMovieSearchPagination] = useState(null);

  // Series
  const [seriesCategories, setSeriesCategories] = useState([]);
  const [loadingSeriesCategories, setLoadingSeriesCategories] = useState(false);
  const [seriesSearchQuery, setSeriesSearchQuery] = useState("");
  const [seriesSearchResults, setSeriesSearchResults] = useState([]);
  const [seriesSearchLoading, setSeriesSearchLoading] = useState(false);
  const [seriesSearchDebounceTimer, setSeriesSearchDebounceTimer] =
    useState(null);
  const [seriesSearchPagination, setSeriesSearchPagination] = useState(null);

  // Favorites
  const [favorites, setFavorites] = useState([]);
  const [processingFavorites, setProcessingFavorites] = useState(new Set());
  const [showAddFavorite, setShowAddFavorite] = useState(false);

  // Profile
  const [profile, setProfile] = useState({
    name: "",
    avatar: null,
  });
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  // Message modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [showRemoveAllModal, setShowRemoveAllModal] = useState(false);
  const [removingAllFavorites, setRemovingAllFavorites] = useState(false);
  const [removalProgress, setRemovalProgress] = useState({
    current: 0,
    total: 0,
  });

  // Check for profile token on mount
  useEffect(() => {
    const profileToken = sessionStorage.getItem(`profile_token_${playlistId}`);
    const profileData = sessionStorage.getItem(`profile_${playlistId}`);

    if (!profileToken || !profileData) {
      // Redirect back to profile selector if no profile is selected
      navigate(`/playlist-manager/${playlistId}`);

      return;
    }

    // Set the selected profile
    try {
      const parsedProfile = JSON.parse(profileData);

      setProfile(parsedProfile);
    } catch (e) {
      console.error("Error parsing profile data:", e);
    }
  }, [playlistId, navigate]);

  useEffect(() => {
    if (token) {
      fetchPlaylistDetails();
      fetchAvailableAvatars();
      fetchFavorites();

      // Load saved category order
      const savedOrder = localStorage.getItem(
        `playlist_category_order_${playlistId}`
      );

      if (savedOrder) {
        try {
          setCategoryOrder(JSON.parse(savedOrder));
        } catch (e) {
          console.error("Error loading category order:", e);
        }
      }
    }
  }, [playlistId, token]);

  // Global ESC key handler for closing modals
  // Fetch categories when channels tab is active
  useEffect(() => {
    if (activeTab === "channels" && !categories.length && playlistId) {
      fetchCategories();
    }
  }, [activeTab, playlistId]);

  // Fetch movie categories when movies tab is active
  useEffect(() => {
    if (activeTab === "movies" && !movieCategories.length && playlistId) {
      fetchMovieCategories();
    }
  }, [activeTab, playlistId]);

  // Fetch series categories when series tab is active
  useEffect(() => {
    if (activeTab === "series" && !seriesCategories.length && playlistId) {
      fetchSeriesCategories();
    }
  }, [activeTab, playlistId]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      // Fetch categories and user info in parallel
      const [categoriesResponse, userInfoResponse] = await Promise.all([
        proxyApiService.getLiveTVCategories(playlistId),
        proxyApiService.getUserInfo(playlistId),
      ]);

      if (categoriesResponse?.data) {
        setCategories(categoriesResponse.data);
      }

      if (userInfoResponse?.data) {
        const userInfo = userInfoResponse.data;

        // Set locked categories from user info
        if (userInfo.settings?.lockedCategories) {
          setLockedCategories(
            new Set(userInfo.settings.lockedCategories.map(String))
          );
        }

        // Set PIN code from user info
        if (userInfo.settings?.pinCode) {
          setUserPinCode(userInfo.settings.pinCode);
        }
      }
    } catch (error) {
      console.error("Failed to fetch categories or user info:", error);
      toast.error("Failed to load channel categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchCategoryChannels = async (categoryId, page = 1) => {
    setLoadingChannels((prev) => ({ ...prev, [categoryId]: true }));
    try {
      const response = await proxyApiService.getLiveTVChannels(playlistId, {
        categories: categoryId,
        page,
        limit: 100,
      });

      if (response?.data) {
        setCategoryChannels((prev) => ({
          ...prev,
          [categoryId]:
            page === 1
              ? response.data
              : [...(prev[categoryId] || []), ...response.data],
        }));

        setCategoryPagination((prev) => ({
          ...prev,
          [categoryId]: {
            currentPage: response.meta?.currentPage || page,
            totalPages: response.meta?.totalPages || 1,
            totalItems: response.meta?.totalItems || 0,
          },
        }));

        // Update channels array for backward compatibility
        const allChannels = response.data.map((channel) => ({
          id: channel.mediaId,
          name: channel.name,
          group:
            categories.find((c) => c.categoryId === categoryId)?.categoryName ||
            "Unknown",
          logo: channel.logoPath || channel.posterPath,
          url: channel.streamUrl,
        }));

        setChannels((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newChannels = allChannels.filter((c) => !existingIds.has(c.id));

          return [...prev, ...newChannels];
        });
      }
    } catch (error) {
      console.error(
        `Failed to fetch channels for category ${categoryId}:`,
        error
      );
      toast.error("Failed to load channels");
    } finally {
      setLoadingChannels((prev) => ({ ...prev, [categoryId]: false }));
    }
  };

  // Movie functions
  const fetchMovieCategories = async () => {
    setLoadingMovieCategories(true);
    try {
      const categoriesResponse =
        await proxyApiService.getMovieCategories(playlistId);

      if (categoriesResponse?.data) {
        // Add unique identifiers to categories if they don't have proper IDs
        const categoriesWithUniqueIds = categoriesResponse.data.map(
          (cat, idx) => ({
            ...cat,
            // Use a combination of categoryId and index for uniqueness
            uniqueId: `movie-${cat.categoryId}-${cat.categoryName}-${idx}`,
            originalCategoryId: cat.categoryId,
          })
        );

        setMovieCategories(categoriesWithUniqueIds);
      }
    } catch (error) {
      console.error("Failed to fetch movie categories:", error);
      toast.error("Failed to load movie categories");
    } finally {
      setLoadingMovieCategories(false);
    }
  };

  // Series functions
  const fetchSeriesCategories = async () => {
    setLoadingSeriesCategories(true);
    try {
      const categoriesResponse =
        await proxyApiService.getSeriesCategories(playlistId);

      if (categoriesResponse?.data) {
        // Add unique identifiers to categories if they don't have proper IDs
        const categoriesWithUniqueIds = categoriesResponse.data.map(
          (cat, idx) => ({
            ...cat,
            // Use a combination of categoryId and index for uniqueness
            uniqueId: `series-${cat.categoryId}-${cat.categoryName}-${idx}`,
            originalCategoryId: cat.categoryId,
          })
        );

        setSeriesCategories(categoriesWithUniqueIds);
      }
    } catch (error) {
      console.error("Failed to fetch series categories:", error);
      toast.error("Failed to load series categories");
    } finally {
      setLoadingSeriesCategories(false);
    }
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        // Close any open modal
        if (showMessageModal) {
          setShowMessageModal(false);
          setMessageContent("");
        }
        if (showAvatarPicker) {
          setShowAvatarPicker(false);
        }
        if (showAddFavorite) {
          setShowAddFavorite(false);
        }
        if (showPinModal) {
          setShowPinModal(false);
          setNewPin("");
          setConfirmNewPin("");
          setShowNewPin(false);
          setShowConfirmPin(false);
          setCurrentPinCategory(null);
          setPinAction(null);
        }
      }
    };

    document.addEventListener("keydown", handleEscKey);

    return () => document.removeEventListener("keydown", handleEscKey);
  }, [showMessageModal, showAvatarPicker, showAddFavorite, showPinModal]);

  const fetchPlaylistDetails = async () => {
    let leftReadOnlyPlaylist = false;

    try {
      // Fetch playlist details from the API
      const response = await apiService.getPlaylists();

      // Find the current playlist
      const currentPlaylist = response.data?.find((p) => p.id === playlistId);

      // Reseller-managed playlists are read-only for the customer, so never
      // show management controls for them, even from a direct URL.
      if (isReadOnlyPlaylistEntry(currentPlaylist)) {
        leftReadOnlyPlaylist = true;
        toast.info(READ_ONLY_PLAYLIST_MESSAGE);
        navigate("/dashboard", { replace: true });

        return;
      }

      if (currentPlaylist) {
        setPlaylist(currentPlaylist);
        // Extract just the domain from the full URL
        if (currentPlaylist.domain) {
          const cleanDomain = currentPlaylist.domain
            .replace(/^https?:\/\//, "")
            .replace(/\/$/, "");

          setDomain(cleanDomain);
        }
      } else {
        // Fallback to dummy data
        setPlaylist({
          id: playlistId,
          name: "My Playlist",
          settings,
          profile,
        });
      }

      // Load channel counts from the setup API (only if profile token exists)
      const profileToken = sessionStorage.getItem(
        `profile_token_${playlistId}`
      );

      if (profileToken) {
        try {
          const setupResponse = await proxyApiService.getSetupInfo(playlistId);

          if (setupResponse?.data) {
            setChannelCounts(setupResponse.data);
          }
        } catch (error) {
          console.error("Error fetching setup info:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching playlist details:", error);
      // Fallback to dummy data on error
      setPlaylist({
        id: playlistId,
        name: "My Playlist",
        settings,
        profile,
      });
    } finally {
      // Always load dummy channels after fetching, unless the page is being
      // left because the playlist is read-only.
      if (!leftReadOnlyPlaylist) {
        loadDummyChannels();
        setLoading(false);
      }
    }
  };

  const fetchFavorites = async () => {
    const profileToken = sessionStorage.getItem(`profile_token_${playlistId}`);

    if (!profileToken) {
      // No profile token, can't fetch favorites
      setFavorites([]);

      return;
    }

    try {
      // Use the proxy API to get bookmarks (favorites)
      const response = await proxyApiService.proxyRequest(
        playlistId,
        "/bookmarks"
      );

      if (response && response.data) {
        // Combine all bookmarks from different media types
        const allFavorites = [];

        // Extract bookmarks from each media type
        if (response.data.liveTv) {
          allFavorites.push(
            ...response.data.liveTv.map((item) => ({
              id: item.mediaId,
              name: item.name,
              logo: item.logoPath || item.posterPath,
              group: "Live TV",
              url: item.streamUrl,
              ...item,
            }))
          );
        }

        if (response.data.movies) {
          allFavorites.push(
            ...response.data.movies.map((item) => ({
              id: item.mediaId,
              name: item.name,
              logo: item.posterPath,
              group: "Movies",
              ...item,
            }))
          );
        }

        if (response.data.series) {
          allFavorites.push(
            ...response.data.series.map((item) => ({
              id: item.mediaId,
              name: item.name,
              logo: item.posterPath,
              group: "Series",
              ...item,
            }))
          );
        }

        // Remove duplicates if any exist (by mediaId)
        const uniqueFavorites = [];
        const seenIds = new Set();

        for (const fav of allFavorites) {
          if (!seenIds.has(fav.id)) {
            seenIds.add(fav.id);
            uniqueFavorites.push(fav);
          }
        }

        setFavorites(uniqueFavorites);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
      // For now, set empty favorites if the API fails
      setFavorites([]);
    }
  };

  const fetchAvailableAvatars = async () => {
    try {
      // Fetch avatars from the API (doesn't require profile token)
      const response = await proxyApiService.getAvatars(playlistId);

      const avatarUrls = response.data || response || [];

      // Convert to the format expected by the component
      const formattedAvatars = avatarUrls.map((url, index) => ({
        id: index + 1,
        url,
      }));

      setAvailableAvatars(formattedAvatars);

      // Set the current profile's avatar as selected
      const profileData = sessionStorage.getItem(`profile_${playlistId}`);

      if (profileData) {
        const parsedProfile = JSON.parse(profileData);

        if (parsedProfile.avatar) {
          const selectedAvatar = formattedAvatars.find(
            (a) => a.url === parsedProfile.avatar
          );

          if (selectedAvatar) {
            setProfile((prev) => ({ ...prev, avatar: selectedAvatar.url }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching avatars:", error);
      // Fallback to default avatars if API fails
      const defaultAvatars = Array.from({ length: 34 }, (_, i) => ({
        id: i + 1,
        url: `https://d1hm15kdjuu43j.cloudfront.net/avatars/avatar_${i + 1}.jpg`,
      }));

      setAvailableAvatars(defaultAvatars);
    }
  };

  const loadDummyChannels = () => {
    const dummyChannels = [
      {
        id: 1,
        name: "CNN International",
        url: "http://example.com/cnn.m3u8",
        group: "News",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/CNN.svg/200px-CNN.svg.png",
      },
      {
        id: 2,
        name: "BBC World News",
        url: "http://example.com/bbc.m3u8",
        group: "News",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/BBC_Logo_2021.svg/200px-BBC_Logo_2021.svg.png",
      },
      {
        id: 3,
        name: "ESPN",
        url: "http://example.com/espn.m3u8",
        group: "Sports",
        logo: "https://a.espncdn.com/combiner/i?img=/i/teamlogos/leagues/500/nba.png&h=200&w=200",
      },
      {
        id: 4,
        name: "Fox Sports 1",
        url: "http://example.com/fox-sports.m3u8",
        group: "Sports",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/2015_Fox_Sports_1_logo.svg/200px-2015_Fox_Sports_1_logo.svg.png",
      },
      {
        id: 5,
        name: "HBO Max",
        url: "http://example.com/hbo.m3u8",
        group: "Entertainment",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/HBO_Max_Logo.svg/200px-HBO_Max_Logo.svg.png",
      },
      {
        id: 6,
        name: "Netflix",
        url: "http://example.com/netflix.m3u8",
        group: "Entertainment",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Netflix_2015_logo.svg/200px-Netflix_2015_logo.svg.png",
      },
      {
        id: 7,
        name: "Discovery Channel",
        url: "http://example.com/discovery.m3u8",
        group: "Documentary",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/27/Discovery_Channel_-_Logo_2019.svg/200px-Discovery_Channel_-_Logo_2019.svg.png",
      },
      {
        id: 8,
        name: "National Geographic",
        url: "http://example.com/natgeo.m3u8",
        group: "Documentary",
        logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/Nat_Geo_Wild_logo.png/200px-Nat_Geo_Wild_logo.png",
      },
      {
        id: 9,
        name: "Cartoon Network",
        url: "http://example.com/cartoon.m3u8",
        group: "Kids",
        logo: "https://upload.wikimedia.org/wikipedia/commons/8/80/Cartoon_Network_2010_logo.svg",
      },
      {
        id: 10,
        name: "Nickelodeon",
        url: "http://example.com/nick.m3u8",
        group: "Kids",
        logo: "https://upload.wikimedia.org/wikipedia/commons/7/7a/Nickelodeon_2009_logo.svg",
      },
      {
        id: 11,
        name: "MTV Music",
        url: "http://example.com/mtv.m3u8",
        group: "Music",
        logo: "https://upload.wikimedia.org/wikipedia/commons/6/68/MTV_2021_%28brand_version%29.svg",
      },
      {
        id: 12,
        name: "VH1 Classic",
        url: "http://example.com/vh1.m3u8",
        group: "Music",
        logo: "https://upload.wikimedia.org/wikipedia/commons/9/94/VH1_logonew.svg",
      },
      {
        id: 13,
        name: "Food Network",
        url: "http://example.com/food.m3u8",
        group: "Lifestyle",
        logo: "https://upload.wikimedia.org/wikipedia/commons/f/f9/Food_Network_New_Logo.png",
      },
      {
        id: 14,
        name: "HGTV",
        url: "http://example.com/hgtv.m3u8",
        group: "Lifestyle",
        logo: "https://upload.wikimedia.org/wikipedia/commons/c/c5/HGTV_US_Logo_2015.svg",
      },
      {
        id: 15,
        name: "Sky News",
        url: "http://example.com/sky-news.m3u8",
        group: "News",
        logo: "https://upload.wikimedia.org/wikipedia/en/thumb/5/57/Sky_News_logo.svg/200px-Sky_News_logo.svg.png",
      },
      {
        id: 16,
        name: "NBC Sports",
        url: "http://example.com/nbc-sports.m3u8",
        group: "Sports",
        logo: "https://www.nbcsports.com/sites/nbcsports.com/files/nbc_sports_logo.png",
      },
      {
        id: 17,
        name: "Comedy Central",
        url: "http://example.com/comedy.m3u8",
        group: "Entertainment",
        logo: "https://upload.wikimedia.org/wikipedia/commons/a/aa/Comedy_Central_2018.svg",
      },
      {
        id: 18,
        name: "Animal Planet",
        url: "http://example.com/animal.m3u8",
        group: "Documentary",
        logo: "https://upload.wikimedia.org/wikipedia/commons/2/20/2018_Animal_Planet_logo.svg",
      },
      {
        id: 19,
        name: "Disney Channel",
        url: "http://example.com/disney.m3u8",
        group: "Kids",
        logo: "https://upload.wikimedia.org/wikipedia/commons/d/d2/2019_Disney_Channel_logo.svg",
      },
      {
        id: 20,
        name: "TLC",
        url: "http://example.com/tlc.m3u8",
        group: "Lifestyle",
        logo: "https://upload.wikimedia.org/wikipedia/commons/7/74/TLC_Logo.svg",
      },
    ];

    setChannels(dummyChannels);
  };

  const handleSettingToggle = () => {
    // Show coming soon message instead of toggling
    toast.info("Feature Coming Soon!");
  };

  const handleClearCache = () => {
    toast.info("Feature Coming Soon!");
  };

  const handleClearData = () => {
    toast.info("Feature Coming Soon!");
  };

  const handleClearRecentlyWatched = () => {
    toast.info("Feature Coming Soon!");
  };

  const handleToggleFavorite = async (media) => {
    try {
      // Determine if this is a new media object (with mediaId) or old format (with id)
      const mediaId = media.mediaId || media.id;

      // Determine media type - check explicit mediaType first, then infer from structure
      let { mediaType } = media;

      if (!mediaType) {
        // Fallback: if it has streamUrl, it's likely live-tv
        if (media.streamUrl) {
          mediaType = "live-tv";
        } else {
          mediaType = "live-tv"; // default fallback
        }
      }

      // Prevent multiple clicks while processing
      if (processingFavorites.has(mediaId)) {
        return;
      }

      // Mark as processing
      setProcessingFavorites((prev) => new Set([...prev, mediaId]));

      // Check if it's currently a favorite (use String comparison)
      const wasInFavorites = favorites.some(
        (f) => String(f.id || f.mediaId) === String(mediaId)
      );

      if (
        mediaType === "live-tv" ||
        mediaType === "movie" ||
        mediaType === "tv"
      ) {
        // Handle all media types using bookmarks API
        if (wasInFavorites) {
          // Remove from favorites
          await proxyApiService.deleteBookmark(
            playlistId,
            String(mediaId),
            mediaType
          );

          // Update local state
          setFavorites((prev) =>
            prev.filter((f) => String(f.id || f.mediaId) !== String(mediaId))
          );

          toast.success("Removed from favorites");
        } else {
          // Add to favorites
          await proxyApiService.createBookmark(
            playlistId,
            String(mediaId),
            mediaType
          );

          // Add to local favorites list
          const groupName =
            mediaType === "live-tv"
              ? "Live TV"
              : mediaType === "movie"
                ? "Movies"
                : "Series";

          const newFavorite = {
            id: mediaId,
            mediaId,
            name: media.name,
            url: media.streamUrl || media.url || "",
            category: media.categoryId || media.group || groupName,
            logo: media.logoPath || media.posterPath || media.logo || "",
            mediaType,
            group: groupName,
          };

          setFavorites((prev) => [...prev, newFavorite]);
          toast.success("Added to favorites");
        }
      } else {
        // Handle old format media (fallback - shouldn't be used)
        // Optimistically update UI
        if (wasInFavorites) {
          setFavorites((prev) =>
            prev.filter((f) => String(f.id) !== String(media.id))
          );
        } else {
          const newFavorite = {
            id: media.id,
            name: media.name,
            url: media.url || "",
            category: media.group || "Uncategorized",
            logo: media.logo || "",
            language: media.language || "",
            country: media.country || "",
            group: media.group || "Uncategorized",
          };

          setFavorites((prev) => [...prev, newFavorite]);
        }

        // Use old API for non-live-tv media
        const mediaData = {
          id: String(media.id),
          name: media.name,
          url: media.url || "",
          category: media.group || "Uncategorized",
          logo: media.logo || "",
          language: media.language || "",
          country: media.country || "",
        };

        const response = await fetch(
          `${API_URL}/v1/dashboard/playlist/favorites`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ channel: mediaData }),
          }
        );

        if (response.ok) {
          const data = await response.json();

          setFavorites(data.favorites || []);
          toast.success(
            wasInFavorites ? "Removed from favorites" : "Added to favorites"
          );
        } else {
          // Revert optimistic update on error
          if (wasInFavorites) {
            setFavorites((prev) => [...prev, mediaData]);
          } else {
            setFavorites((prev) =>
              prev.filter((f) => String(f.id) !== String(media.id))
            );
          }
          throw new Error("Failed to update favorites");
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorites");
    } finally {
      // Remove from processing
      const mediaIdToRemove = media.mediaId || media.id;

      setProcessingFavorites((prev) => {
        const newSet = new Set(prev);

        newSet.delete(mediaIdToRemove);

        return newSet;
      });
    }
  };

  const handleRemoveFavorite = async (channel) => {
    try {
      // Extract mediaType from the channel data
      let { mediaType } = channel;

      if (!mediaType) {
        if (channel.group === "Movies") {
          mediaType = "movie";
        } else if (channel.group === "TV Shows" || channel.group === "Series") {
          mediaType = "tv";
        } else {
          mediaType = "live-tv";
        }
      }

      await proxyApiService.deleteBookmark(
        playlistId,
        String(channel.id),
        mediaType
      );

      // Remove from local state
      setFavorites(favorites.filter((f) => f.id !== channel.id));
      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error("Failed to remove favorite");
    }
  };

  const handleRemoveAllFavorites = async () => {
    if (favorites.length === 0) {
      toast.info("No favorites to remove");

      return;
    }

    setShowRemoveAllModal(true);
  };

  const confirmRemoveAllFavorites = async () => {
    try {
      setRemovingAllFavorites(true);
      setRemovalProgress({ current: 0, total: favorites.length });

      const failedRemovals = [];

      // Loop through each favorite and delete it
      for (let i = 0; i < favorites.length; i++) {
        const favorite = favorites[i];

        setRemovalProgress({ current: i + 1, total: favorites.length });

        try {
          // Extract mediaType from the favorite data
          let { mediaType } = favorite;

          if (!mediaType) {
            if (favorite.group === "Movies") {
              mediaType = "movie";
            } else if (
              favorite.group === "TV Shows" ||
              favorite.group === "Series"
            ) {
              mediaType = "tv";
            } else {
              mediaType = "live-tv";
            }
          }

          await proxyApiService.deleteBookmark(
            playlistId,
            String(favorite.id),
            mediaType
          );

          // Small delay to avoid overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`Failed to remove favorite: ${favorite.name}`, error);
          failedRemovals.push(favorite.name);
        }
      }

      if (failedRemovals.length > 0) {
        toast.warning(
          `Removed ${favorites.length - failedRemovals.length} of ${favorites.length} favorites`
        );
      } else {
        toast.success("All favorites removed successfully");
      }

      // Clear local state
      setFavorites([]);
      setShowRemoveAllModal(false);
    } catch (error) {
      console.error("Error removing all favorites:", error);
      toast.error("Failed to remove favorites");
    } finally {
      setRemovingAllFavorites(false);
      setRemovalProgress({ current: 0, total: 0 });
      // Reload favorites to ensure sync
      fetchFavorites();
    }
  };

  const isFavorite = (channelId) =>
    // Convert to string for comparison to handle both string and number IDs
    // Check both id and mediaId fields for compatibility
    favorites.some((f) => String(f.id || f.mediaId) === String(channelId));
  const handleToggleCategoryHide = (category) => {
    const newHidden = new Set(hiddenCategories);

    if (newHidden.has(category)) {
      newHidden.delete(category);
      toast.success(`${category} category shown`);
    } else {
      newHidden.add(category);
      toast.success(`${category} category hidden`);
    }
    setHiddenCategories(newHidden);
  };

  const handleSetCategoryPin = async (categoryId) => {
    // Check if PIN is set
    if (!userPinCode) {
      // No PIN set, prompt user to create one
      toast.warning("Please set a PIN first to lock categories");
      // Store the category to lock after PIN is set
      setCurrentPinCategory(categoryId);
      setPinAction("setAndLock");
      setNewPin("");
      setConfirmNewPin("");
      setShowNewPin(false);
      setShowConfirmPin(false);
      setShowPinModal(true);

      return;
    }

    // Lock a category (PIN exists)
    try {
      const newLockedCategories = new Set(lockedCategories);

      newLockedCategories.add(String(categoryId));

      // Call API to update locked categories
      const lockedCategoriesArray = Array.from(newLockedCategories);

      await proxyApiService.updateUserSettings(playlistId, {
        lockedCategories: lockedCategoriesArray,
      });

      // Update local state after successful API call
      setLockedCategories(newLockedCategories);

      // Find category name for toast message
      const category = categories.find((cat) => cat.categoryId === categoryId);
      const categoryName = category ? category.categoryName : "Category";

      toast.success(`${categoryName} locked successfully`);
    } catch (error) {
      console.error("Failed to lock category:", error);
      toast.error("Failed to lock category");
    }
  };

  const handleUnlockCategory = async (categoryId) => {
    // Unlock a category
    try {
      const newLockedCategories = new Set(lockedCategories);

      newLockedCategories.delete(String(categoryId));

      // Call API to update locked categories
      const lockedCategoriesArray = Array.from(newLockedCategories);

      await proxyApiService.updateUserSettings(playlistId, {
        lockedCategories: lockedCategoriesArray,
      });

      // Update local state after successful API call
      setLockedCategories(newLockedCategories);

      // Find category name for toast message
      const category = categories.find((cat) => cat.categoryId === categoryId);
      const categoryName = category ? category.categoryName : "Category";

      toast.success(`${categoryName} unlocked successfully`);
    } catch (error) {
      console.error("Failed to unlock category:", error);
      toast.error("Failed to unlock category");
    }
  };

  const handleResetPin = () => {
    setPinAction("reset");
    setNewPin("");
    setConfirmNewPin("");
    setShowNewPin(false);
    setShowConfirmPin(false);
    setShowPinModal(true);
  };

  const handleVerifyPin = async () => {
    // Handle both reset and setAndLock actions
    if (pinAction !== "reset" && pinAction !== "setAndLock") return;

    // Validate new PIN
    if (newPin.length !== 4) {
      toast.error("New PIN must be exactly 4 digits");

      return;
    }

    if (newPin !== confirmNewPin) {
      toast.error("New PIN codes do not match");

      return;
    }

    try {
      // Call API to update PIN code
      await proxyApiService.updateUserSettings(playlistId, {
        pinCode: newPin,
      });

      // Update local state
      setUserPinCode(newPin);

      if (pinAction === "setAndLock" && currentPinCategory) {
        // After setting PIN, lock the category
        const newLockedCategories = new Set(lockedCategories);

        newLockedCategories.add(String(currentPinCategory));

        // Call API to update locked categories
        const lockedCategoriesArray = Array.from(newLockedCategories);

        await proxyApiService.updateUserSettings(playlistId, {
          lockedCategories: lockedCategoriesArray,
        });

        // Update local state
        setLockedCategories(newLockedCategories);

        // Find category name for toast message
        const category = categories.find(
          (cat) => cat.categoryId === currentPinCategory
        );
        const categoryName = category ? category.categoryName : "Category";

        toast.success(`PIN set and ${categoryName} locked successfully`);
      } else {
        toast.success("PIN code updated successfully");
      }

      setShowPinModal(false);
      setNewPin("");
      setConfirmNewPin("");
      setShowNewPin(false);
      setShowConfirmPin(false);
      setCurrentPinCategory(null);
    } catch (error) {
      console.error("Failed to update PIN code:", error);
      toast.error("Failed to update PIN code");
    }
  };

  const handleRemovePin = async () => {
    try {
      // Call API to remove PIN code by setting it to null
      await proxyApiService.updateUserSettings(playlistId, {
        pinCode: null,
      });

      // Update local state
      setUserPinCode("");

      // Unlock all categories
      await proxyApiService.updateUserSettings(playlistId, {
        lockedCategories: [],
      });
      setLockedCategories(new Set());

      toast.success("PIN removed and all categories unlocked");

      setShowPinModal(false);
      setNewPin("");
      setConfirmNewPin("");
      setShowNewPin(false);
      setShowConfirmPin(false);
      setCurrentPinCategory(null);
    } catch (error) {
      console.error("Failed to remove PIN code:", error);
      toast.error("Failed to remove PIN code");
    }
  };

  const handleToggleCategory = async (categoryId) => {
    // If this category is already expanded, collapse it
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);

      return;
    }

    // Collapse any other expanded category and expand this one
    setExpandedCategory(categoryId);

    // Fetch channels if not already loaded
    if (!categoryChannels[categoryId]) {
      await fetchCategoryChannels(categoryId);
    }
  };

  const loadMoreChannels = async (categoryId) => {
    const pagination = categoryPagination[categoryId];

    if (!pagination || pagination.currentPage >= pagination.totalPages) return;

    await fetchCategoryChannels(categoryId, pagination.currentPage + 1);
  };

  const searchChannels = async (query, page = 1) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchPagination(null);

      return;
    }

    setSearchLoading(true);
    try {
      const response = await proxyApiService.searchChannels(
        playlistId,
        query,
        page,
        100
      );

      if (response?.data) {
        if (page === 1) {
          setSearchResults(response.data);
        } else {
          setSearchResults((prev) => [...prev, ...response.data]);
        }

        if (response.meta) {
          setSearchPagination({
            currentPage: response.meta.currentPage || page,
            totalPages: response.meta.totalPages || 1,
            totalItems: response.meta.totalItems || response.data.length,
          });
        }
      }
    } catch (error) {
      console.error("Failed to search channels:", error);
      toast.error("Failed to search channels");
    } finally {
      setSearchLoading(false);
    }
  };

  const loadMoreSearchResults = async () => {
    if (
      !searchQuery ||
      !searchPagination ||
      searchPagination.currentPage >= searchPagination.totalPages
    ) {
      return;
    }
    await searchChannels(searchQuery, searchPagination.currentPage + 1);
  };

  const searchMovies = async (query, page = 1) => {
    if (!query.trim()) {
      setMovieSearchResults([]);
      setMovieSearchPagination(null);

      return;
    }

    setMovieSearchLoading(true);
    try {
      const response = await proxyApiService.searchMovies(
        playlistId,
        query,
        page,
        100
      );

      if (response?.data) {
        // Ensure each movie has the correct mediaType
        const moviesWithType = response.data.map((movie) => ({
          ...movie,
          mediaType: movie.mediaType || "movie",
        }));

        if (page === 1) {
          setMovieSearchResults(moviesWithType);
        } else {
          setMovieSearchResults((prev) => [...prev, ...moviesWithType]);
        }

        if (response.meta) {
          setMovieSearchPagination({
            currentPage: response.meta.currentPage || page,
            totalPages: response.meta.totalPages || 1,
            totalItems: response.meta.totalItems || response.data.length,
          });
        }
      }
    } catch (error) {
      console.error("Failed to search movies:", error);
      toast.error("Failed to search movies");
    } finally {
      setMovieSearchLoading(false);
    }
  };

  const loadMoreMovieSearchResults = async () => {
    if (
      !movieSearchQuery ||
      !movieSearchPagination ||
      movieSearchPagination.currentPage >= movieSearchPagination.totalPages
    ) {
      return;
    }
    await searchMovies(movieSearchQuery, movieSearchPagination.currentPage + 1);
  };

  const handleMovieSearchChange = (value) => {
    setMovieSearchQuery(value);

    if (movieSearchDebounceTimer) {
      clearTimeout(movieSearchDebounceTimer);
    }

    if (!value.trim()) {
      setMovieSearchResults([]);
      setMovieSearchPagination(null);

      return;
    }

    const timer = setTimeout(() => {
      searchMovies(value);
    }, 500);

    setMovieSearchDebounceTimer(timer);
  };

  const searchSeries = async (query, page = 1) => {
    if (!query.trim()) {
      setSeriesSearchResults([]);
      setSeriesSearchPagination(null);

      return;
    }

    setSeriesSearchLoading(true);
    try {
      const response = await proxyApiService.searchSeries(
        playlistId,
        query,
        page,
        100
      );

      if (response?.data) {
        // Ensure each series has the correct mediaType
        const seriesWithType = response.data.map((series) => ({
          ...series,
          mediaType: series.mediaType || "tv",
        }));

        if (page === 1) {
          setSeriesSearchResults(seriesWithType);
        } else {
          setSeriesSearchResults((prev) => [...prev, ...seriesWithType]);
        }

        if (response.meta) {
          setSeriesSearchPagination({
            currentPage: response.meta.currentPage || page,
            totalPages: response.meta.totalPages || 1,
            totalItems: response.meta.totalItems || response.data.length,
          });
        }
      }
    } catch (error) {
      console.error("Failed to search series:", error);
      toast.error("Failed to search series");
    } finally {
      setSeriesSearchLoading(false);
    }
  };

  const loadMoreSeriesSearchResults = async () => {
    if (
      !seriesSearchQuery ||
      !seriesSearchPagination ||
      seriesSearchPagination.currentPage >= seriesSearchPagination.totalPages
    ) {
      return;
    }
    await searchSeries(
      seriesSearchQuery,
      seriesSearchPagination.currentPage + 1
    );
  };

  const handleSeriesSearchChange = (value) => {
    setSeriesSearchQuery(value);

    if (seriesSearchDebounceTimer) {
      clearTimeout(seriesSearchDebounceTimer);
    }

    if (!value.trim()) {
      setSeriesSearchResults([]);
      setSeriesSearchPagination(null);

      return;
    }

    const timer = setTimeout(() => {
      searchSeries(value);
    }, 500);

    setSeriesSearchDebounceTimer(timer);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);

    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // If empty, clear results immediately
    if (!value.trim()) {
      setSearchResults([]);
      setSearchPagination(null);

      return;
    }

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      searchChannels(value);
    }, 500); // 500ms debounce

    setSearchDebounceTimer(timer);
  };

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    },
    [searchDebounceTimer]
  );

  const handleSendMessage = () => {
    if (!messageContent.trim()) {
      toast.error("Please enter a message");

      return;
    }

    // Here you would normally send the message to the backend
    toast.success(`Message sent to ${profile.name || user?.name || "User"}`);
    setShowMessageModal(false);
    setMessageContent("");
  };

  const getChannelsByCategory = () => {
    const grouped = {};

    // Filter channels based on search query
    const filteredChannels = channels.filter((channel) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();

      return (
        channel.name.toLowerCase().includes(query) ||
        (channel.group && channel.group.toLowerCase().includes(query))
      );
    });

    filteredChannels.forEach((channel) => {
      const group = channel.group || "Uncategorized";

      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(channel);
    });

    return grouped;
  };

  // Get sorted categories based on saved order
  const getSortedCategories = () => {
    const categories = Object.entries(getChannelsByCategory());

    // If no custom order exists, initialize it with current categories
    if (categoryOrder.length === 0) {
      const defaultOrder = categories.map(([cat]) => cat);

      setCategoryOrder(defaultOrder);
      localStorage.setItem(
        `playlist_category_order_${playlistId}`,
        JSON.stringify(defaultOrder)
      );

      return categories;
    }

    // Sort categories based on saved order
    return categories.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a[0]);
      const indexB = categoryOrder.indexOf(b[0]);

      // If category not in order (new category), put at end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  };

  // Handle drag end for categories
  const handleCategoryDragEnd = (result) => {
    if (!result.destination) return;

    const visibleCategories = getSortedCategories()
      .filter(([category]) => {
        const isHidden = hiddenCategories.has(category);

        return !isHidden || showHiddenCategories;
      })
      .map(([category]) => category);

    const items = Array.from(visibleCategories);
    const [reorderedItem] = items.splice(result.source.index, 1);

    items.splice(result.destination.index, 0, reorderedItem);

    // Update full category order
    const newOrder = [...categoryOrder];
    const oldIndex = newOrder.indexOf(reorderedItem);

    if (oldIndex !== -1) {
      newOrder.splice(oldIndex, 1);
    }

    // Find the correct position in full order
    if (result.destination.index === 0) {
      newOrder.unshift(reorderedItem);
    } else if (result.destination.index >= items.length - 1) {
      newOrder.push(reorderedItem);
    } else {
      const prevItem = items[result.destination.index - 1];
      const prevIndex = newOrder.indexOf(prevItem);

      newOrder.splice(prevIndex + 1, 0, reorderedItem);
    }

    setCategoryOrder(newOrder);
    localStorage.setItem(
      `playlist_category_order_${playlistId}`,
      JSON.stringify(newOrder)
    );
    toast.success("Category order updated");
  };

  const handleAvatarSelect = async (avatar) => {
    try {
      // Get the current profile from sessionStorage
      const profileData = sessionStorage.getItem(`profile_${playlistId}`);

      if (!profileData) {
        toast.error("Profile information not found");

        return;
      }

      const parsedProfile = JSON.parse(profileData);

      // Update the profile via API
      await proxyApiService.updateProfile(playlistId, parsedProfile.id, {
        profileName: parsedProfile.name,
        avatarUrl: avatar.url,
      });

      // Update local state
      setProfile({ ...profile, avatar: avatar.url });

      // Update sessionStorage with new avatar
      const updatedProfile = { ...parsedProfile, avatar: avatar.url };

      sessionStorage.setItem(
        `profile_${playlistId}`,
        JSON.stringify(updatedProfile)
      );

      setShowAvatarPicker(false);
      toast.success("Avatar updated successfully");
    } catch (error) {
      console.error("Error updating avatar:", error);
      toast.error("Failed to update avatar");
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{
          backgroundImage: "url(/background.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            Loading playlist details...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-scroll md:bg-fixed"
      style={{
        backgroundImage: "url(/background.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 px-4 py-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => navigate(`/playlist-manager/${playlistId}`)}
          >
            <i className="fas fa-arrow-left mr-2" />
            Back to Profiles
          </button>

          <div className="flex items-center justify-between mb-6">
            {/* Left side - Profile with name and email */}
            <div className="flex items-center gap-4">
              <div
                className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity shadow-lg"
                onClick={() => setShowAvatarPicker(true)}
              >
                {profile.avatar ? (
                  <img
                    alt="Avatar"
                    className="w-full h-full rounded-full object-cover"
                    src={profile.avatar}
                  />
                ) : (
                  <i className="fas fa-user text-white text-2xl" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {profile.name || user?.name || "Jack Nicholson"}
                </h1>
                <p className="text-sm text-gray-400 mt-1">
                  {domain || playlist?.domain || "Loading..."}
                </p>
              </div>
            </div>

            {/* Right side - LunaKey */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end">
                <span className="text-xs sm:text-base text-white mb-1 sm:mb-2">
                  LunaKey™
                </span>
                <div className="flex items-center gap-2">
                  <code
                    className="px-2 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white rounded-lg font-mono text-base sm:text-lg font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                    onClick={() => {
                      const code =
                        playlist?.code || playlist?.playlistCode || "";

                      navigator.clipboard.writeText(code);
                      toast.success("Copied!");
                    }}
                  >
                    {playlist?.code ||
                      playlist?.playlistCode ||
                      "XXXX-XXXX-XXXX"}
                  </code>
                  <button
                    className="p-2.5 hover:bg-gray-700 rounded-lg transition-colors hidden sm:block"
                    title="Copy LunaKey™"
                    onClick={() => {
                      const code =
                        playlist?.code || playlist?.playlistCode || "";

                      navigator.clipboard.writeText(code);
                      toast.success("LunaKey™ copied!");
                    }}
                  >
                    <i className="fas fa-copy text-gray-400 text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap sm:flex-nowrap gap-2 mb-6 p-2 sm:p-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-3xl sm:rounded-full w-full sm:w-fit">
          {[
            "overview",
            "channels",
            "movies",
            "series",
            "favorites",
            "settings",
          ].map((tab) => (
            <button
              className={`px-4 sm:px-5 py-2.5 font-medium capitalize transition-all rounded-full w-[calc(50%-0.25rem)] sm:w-auto ${
                activeTab === tab
                  ? "bg-white dark:bg-gray-800/30 text-blue-500 dark:text-blue-400 shadow-md transform sm:scale-105"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50/50"
              }`}
              key={tab}
              onClick={() => setActiveTab(tab)}
            >
              <div className="flex items-center gap-2">
                <i
                  className={`${
                    tab === "overview"
                      ? "fas fa-th-large"
                      : tab === "channels"
                        ? "fas fa-tv"
                        : tab === "movies"
                          ? "fas fa-film"
                          : tab === "series"
                            ? "fas fa-video"
                            : tab === "favorites"
                              ? "fas fa-star"
                              : tab === "settings"
                                ? "fas fa-cog"
                                : "fas fa-file-code"
                  } text-sm`}
                />
                <span className="text-sm sm:text-base">{tab}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: 20 }}
              key="overview"
            >
              {/* Stats Cards Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div
                  className="card p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 shadow-md cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
                  title="Click to view channels"
                  onClick={() => setActiveTab("channels")}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Live TV
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {categories.length > 0
                          ? categories
                              .reduce(
                                (sum, cat) => sum + (cat.itemCount || 0),
                                0
                              )
                              .toLocaleString()
                          : channelCounts.liveTv.toLocaleString()}
                      </p>
                    </div>
                    <i className="fas fa-tv text-blue-600 dark:text-blue-400 text-2xl pr-2.5" />
                  </div>
                </div>

                <div
                  className="card p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 shadow-md cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
                  title="Click to view movies"
                  onClick={() => setActiveTab("movies")}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Movies
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {channelCounts.movies.toLocaleString()}
                      </p>
                    </div>
                    <i className="fas fa-film text-purple-600 dark:text-purple-400 text-2xl pr-2.5" />
                  </div>
                </div>

                <div
                  className="card p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 shadow-md cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
                  title="Click to view series"
                  onClick={() => setActiveTab("series")}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Series
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {channelCounts.series.toLocaleString()}
                      </p>
                    </div>
                    <i className="fas fa-video text-yellow-600 dark:text-yellow-400 text-2xl pr-2.5" />
                  </div>
                </div>

                <div className="card p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 shadow-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Total Content
                      </p>
                      <p className="text-2xl font-bold mt-1">
                        {channelCounts.total.toLocaleString()}
                      </p>
                    </div>
                    <i className="fas fa-play-circle text-green-600 dark:text-green-400 text-2xl pr-2.5" />
                  </div>
                </div>
              </div>

              {/* Main Content Grid - Favorites, Quick Settings, and Quick Actions Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-6">
                {/* Favorite Channels */}
                <div className="card shadow-md">
                  <div
                    className={`py-1.5 px-3 sm:p-4 ${favorites.length > 0 ? "border-b border-gray-200 dark:border-gray-700" : "sm:border-b sm:border-gray-200 sm:dark:border-gray-700"}`}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className="text-base sm:text-lg font-semibold pr-[10px]">
                        <i className="fas fa-star text-yellow-500 mr-2 text-base" />
                        <span className="hidden sm:inline">
                          Favorite Channels
                        </span>
                        <span className="sm:hidden">Favorites</span>
                      </h3>
                      <button
                        className="text-sm text-blue-500 hover:text-blue-600"
                        onClick={() => setActiveTab("favorites")}
                      >
                        <span className="hidden sm:inline">View All</span>{" "}
                        <i className="fas fa-arrow-right ml-1" />
                      </button>
                    </div>
                  </div>
                  {favorites.length > 0 ? (
                    <div className="hidden sm:block py-1.5 px-3 sm:p-4">
                      <div className="grid grid-cols-2 gap-2">
                        {favorites.slice(0, 4).map((channel) => (
                          <div
                            className="p-2 bg-gray-700/40 backdrop-blur-sm border border-gray-600/50 rounded-lg cursor-pointer hover:bg-gray-700/50 hover:shadow-md transition-all"
                            key={channel.id}
                            onClick={() => setActiveTab("favorites")}
                          >
                            <div className="flex items-center gap-2">
                              {channel.logo ? (
                                <div className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-900 rounded p-1">
                                  <img
                                    alt={channel.name}
                                    className="max-w-full max-h-full object-contain"
                                    src={channel.logo}
                                  />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                  <i className="fas fa-tv text-xs" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {channel.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {channel.group}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {favorites.length > 4 && (
                        <p className="text-center text-sm text-gray-500 mt-3">
                          +{favorites.length - 4} more favorites
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Quick Settings */}
                <div className="card shadow-md">
                  <div className="py-1.5 px-3 sm:p-4 sm:border-b sm:border-gray-200 sm:dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <h3 className="text-base sm:text-lg font-semibold">
                        Quick Settings
                      </h3>
                      <button
                        className="text-sm text-blue-500 hover:text-blue-600"
                        onClick={() => setActiveTab("settings")}
                      >
                        <span className="hidden sm:inline">View All</span>{" "}
                        <i className="fas fa-arrow-right ml-1" />
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:block py-1 px-3 sm:p-4 sm:space-y-2">
                    <label className="flex items-center justify-between cursor-pointer sm:mb-0">
                      <span className="text-xs sm:text-sm">
                        Hardware Decoder
                      </span>
                      <div className="toggle-switch">
                        <input
                          checked={settings.useHardwareDecoder}
                          type="checkbox"
                          onChange={() =>
                            handleSettingToggle("useHardwareDecoder")
                          }
                        />
                        <span className="toggle-bg" />
                        <span className="toggle-handle" />
                      </div>
                    </label>
                    <label className="hidden sm:flex items-center justify-between cursor-pointer">
                      <span className="text-sm">Closed Captions</span>
                      <div className="toggle-switch">
                        <input
                          checked={settings.closedCaptions}
                          type="checkbox"
                          onChange={() => handleSettingToggle("closedCaptions")}
                        />
                        <span className="toggle-bg" />
                        <span className="toggle-handle" />
                      </div>
                    </label>
                    <label className="hidden sm:flex items-center justify-between cursor-pointer">
                      <span className="text-sm">Auto Clear Cache</span>
                      <div className="toggle-switch">
                        <input
                          checked={settings.clearCacheAuto}
                          type="checkbox"
                          onChange={() => handleSettingToggle("clearCacheAuto")}
                        />
                        <span className="toggle-bg" />
                        <span className="toggle-handle" />
                      </div>
                    </label>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="card shadow-md">
                  <div className="py-1.5 px-3 sm:p-4 sm:border-b sm:border-gray-200 sm:dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <h3 className="text-base sm:text-lg font-semibold">
                        Quick Actions
                      </h3>
                      <button
                        className="text-sm text-blue-500 hover:text-blue-600 sm:hidden"
                        onClick={() => setActiveTab("settings")}
                      >
                        <i className="fas fa-arrow-right ml-1" />
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:block py-1 px-3 sm:p-4 space-y-1">
                    <button
                      className="w-full text-left p-1.5 sm:p-3 bg-gray-700/40 hover:bg-gray-700/60 rounded transition-all"
                      onClick={handleClearCache}
                    >
                      <div className="text-xs sm:text-sm font-medium text-white">
                        Clear Cache
                      </div>
                    </button>
                    <button
                      className="hidden sm:block w-full text-left p-3 bg-gray-700/40 hover:bg-gray-700/60 rounded-lg transition-all"
                      onClick={handleClearRecentlyWatched}
                    >
                      <div className="font-semibold text-white">
                        Clear Recently Watched
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Reset your viewing history
                      </div>
                    </button>
                    <button
                      className="hidden sm:block w-full text-left p-3 bg-red-900/20 hover:bg-red-900/30 border border-red-800/50 rounded-lg transition-all"
                      onClick={handleClearData}
                    >
                      <div className="font-semibold text-red-400">
                        Clear All Data
                      </div>
                      <div className="text-xs text-red-400/70 mt-0.5">
                        Remove all app data
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "channels" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: 20 }}
              key="channels"
            >
              <div className="space-y-4">
                {/* Header Stats */}
                <div className="card p-4 shadow-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold">Channel Library</h3>
                      <div className="flex gap-4 text-sm text-gray-500 mt-1">
                        <span>
                          <i className="fas fa-tv mr-1" /> Total Channels:{" "}
                          {categories
                            .reduce((sum, cat) => sum + (cat.itemCount || 0), 0)
                            .toLocaleString()}
                        </span>
                        <span>
                          <i className="fas fa-folder mr-1" /> Categories:{" "}
                          {categories.length}
                        </span>
                        <span>
                          <i className="fas fa-eye-slash mr-1" /> Hidden:{" "}
                          {hiddenCategories.size}
                        </span>
                        <span>
                          <i className="fas fa-lock mr-1" /> Locked:{" "}
                          {Object.keys(lockedCategories).length}
                        </span>
                        <span>
                          <i className="fas fa-star mr-1" /> Favorites:{" "}
                          {favorites.length}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          className="input pl-10 pr-4 py-2 text-sm w-64"
                          placeholder="Search channels..."
                          type="text"
                          value={searchQuery}
                          onChange={(e) => handleSearchChange(e.target.value)}
                        />
                        <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        {searchQuery && (
                          <button
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults([]);
                              setSearchPagination(null);
                            }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>
                      {hiddenCategories.size > 0 && (
                        <button
                          className={`btn btn-sm ${showHiddenCategories ? "btn-warning" : "btn-ghost"}`}
                          onClick={() =>
                            setShowHiddenCategories(!showHiddenCategories)
                          }
                        >
                          <i className="fas fa-eye mr-2" />
                          {showHiddenCategories ? "Hide" : "Show"} Hidden (
                          {hiddenCategories.size})
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-ghost"
                        title="Reset PIN code"
                        onClick={() => handleResetPin()}
                      >
                        <i className="fas fa-key mr-2" />
                        Reset PIN
                      </button>
                    </div>
                  </div>
                </div>

                {/* Categorized Channels */}
                {loadingCategories ? (
                  <div className="card p-8 shadow-md">
                    <div className="text-center">
                      <LoadingSpinner size="lg" />
                      <p className="text-gray-500 mt-3">
                        Loading channel categories...
                      </p>
                    </div>
                  </div>
                ) : searchQuery ? (
                  // Show search results when searching
                  <div className="space-y-3">
                    {searchLoading ? (
                      <div className="card p-8 shadow-md">
                        <div className="text-center">
                          <LoadingSpinner size="lg" />
                          <p className="text-gray-500 mt-3">
                            Searching for "{searchQuery}"...
                          </p>
                        </div>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="card p-4 shadow-md">
                        <div className="mb-4">
                          <h4 className="font-semibold text-lg">
                            Search Results ({searchResults.length} channels)
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {searchResults.map((channel) => {
                            const isFav = isFavorite(channel.mediaId);
                            const category = categories.find(
                              (cat) => cat.categoryId === channel.categoryId
                            );

                            return (
                              <div
                                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                                key={channel.mediaId}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-white dark:bg-gray-800/30 rounded-lg shadow-sm flex items-center justify-center">
                                    {channel.logoPath || channel.posterPath ? (
                                      <img
                                        alt={channel.name}
                                        className="w-8 h-8 rounded object-contain"
                                        src={
                                          channel.logoPath || channel.posterPath
                                        }
                                      />
                                    ) : (
                                      <i className="fas fa-tv text-gray-500" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium flex items-center gap-2">
                                      {channel.name}
                                      {isFav && (
                                        <i className="fas fa-star text-yellow-500 text-xs" />
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                        {category
                                          ? category.categoryName
                                          : "Live TV"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    className={`p-1.5 rounded transition-all transform ${
                                      processingFavorites.has(channel.mediaId)
                                        ? "opacity-50 cursor-not-allowed"
                                        : isFav
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 scale-110"
                                          : "hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:scale-105"
                                    }`}
                                    disabled={processingFavorites.has(
                                      channel.mediaId
                                    )}
                                    title={
                                      isFav
                                        ? "Remove from favorites"
                                        : "Add to favorites"
                                    }
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleFavorite(channel);
                                    }}
                                  >
                                    {processingFavorites.has(
                                      channel.mediaId
                                    ) ? (
                                      <LoadingSpinner size="xs" />
                                    ) : (
                                      <i
                                        className={`${isFav ? "fas" : "far"} fa-star ${
                                          isFav
                                            ? "text-yellow-500"
                                            : "text-gray-400"
                                        }`}
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Pagination for search results */}
                        {searchPagination &&
                          searchPagination.totalPages > 1 &&
                          searchPagination.currentPage <
                            searchPagination.totalPages && (
                            <div className="mt-4 flex justify-center">
                              <button
                                className="btn btn-sm"
                                disabled={searchLoading}
                                onClick={loadMoreSearchResults}
                              >
                                {searchLoading ? (
                                  <>
                                    <LoadingSpinner size="sm" />
                                    <span className="ml-2">Loading...</span>
                                  </>
                                ) : (
                                  <>
                                    Load More ({searchResults.length} of{" "}
                                    {searchPagination.totalItems})
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="card p-8 shadow-md">
                        <div className="text-center">
                          <i className="fas fa-search text-4xl text-gray-300 mb-3" />
                          <p className="text-gray-500">
                            No channels found matching "{searchQuery}"
                          </p>
                          <button
                            className="btn btn-ghost btn-sm mt-4"
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults([]);
                              setSearchPagination(null);
                            }}
                          >
                            Clear Search
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : categories.length > 0 ? (
                  // Show categories when not searching
                  <div className="space-y-3">
                    <DragDropContext onDragEnd={handleCategoryDragEnd}>
                      <StrictModeDroppable droppableId="categories">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            className="space-y-3"
                            ref={provided.innerRef}
                          >
                            {categories.map((category, index) => {
                              const { categoryId } = category;
                              const { categoryName } = category;
                              const channelCount = category.itemCount;
                              const isExpanded =
                                expandedCategory === categoryId;
                              const isLoading = loadingChannels[categoryId];
                              const channels =
                                categoryChannels[categoryId] || [];
                              const pagination = categoryPagination[categoryId];
                              const isHidden = hiddenCategories.has(categoryId);
                              const isLocked = lockedCategories.has(
                                String(categoryId)
                              );

                              // Skip hidden categories unless we're showing them
                              if (isHidden && !showHiddenCategories)
                                return null;

                              return (
                                <Draggable
                                  draggableId={categoryId}
                                  index={index}
                                  key={categoryId}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`card overflow-hidden bg-gray-800/60 backdrop-blur-sm ${
                                        isLocked
                                          ? "border-2 border-red-300 dark:border-red-800"
                                          : ""
                                      } ${isHidden ? "opacity-75 border-2 border-yellow-300 dark:border-yellow-800" : ""} ${
                                        snapshot.isDragging
                                          ? "shadow-2xl opacity-90"
                                          : ""
                                      }`}
                                    >
                                      {/* Category Header */}
                                      <div className="p-4 bg-gray-800/70 backdrop-blur-sm border border-gray-700/50">
                                        <div className="flex items-center justify-between">
                                          {/* Drag Handle */}
                                          <div
                                            {...provided.dragHandleProps}
                                            className="cursor-move p-2 mr-2 text-gray-400 hover:text-gray-600"
                                          >
                                            <i className="fas fa-grip-vertical" />
                                          </div>
                                          <button
                                            className="flex items-center gap-3 flex-1"
                                            onClick={() =>
                                              handleToggleCategory(categoryId)
                                            }
                                          >
                                            <i
                                              className={`fas fa-chevron-${!isExpanded ? "right" : "down"} text-blue-500`}
                                            />
                                            <h4 className="font-semibold text-lg">
                                              {categoryName}
                                            </h4>
                                            <span className="text-sm text-gray-500">
                                              ({channelCount} channels)
                                            </span>
                                            {isHidden && (
                                              <span className="badge badge-warning ml-2">
                                                <i className="fas fa-eye-slash mr-1" />
                                                Hidden
                                              </span>
                                            )}
                                            {isLocked && (
                                              <i className="fas fa-lock text-red-500 ml-2" />
                                            )}
                                          </button>

                                          <div className="flex items-center gap-2">
                                            {channels.filter((c) =>
                                              isFavorite(c.mediaId)
                                            ).length > 0 && (
                                              <span className="badge badge-warning">
                                                <i className="fas fa-star mr-1" />
                                                {
                                                  channels.filter((c) =>
                                                    isFavorite(c.mediaId)
                                                  ).length
                                                }
                                              </span>
                                            )}

                                            {/* Category Controls */}
                                            <button
                                              className={`p-2 rounded ${
                                                isHidden
                                                  ? "text-green-600 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/20"
                                                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                                              }`}
                                              title={
                                                isHidden
                                                  ? "Unhide category"
                                                  : "Hide category"
                                              }
                                              onClick={() =>
                                                handleToggleCategoryHide(
                                                  categoryId
                                                )
                                              }
                                            >
                                              <i
                                                className={`fas ${isHidden ? "fa-eye" : "fa-eye-slash"} text-sm`}
                                              />
                                            </button>

                                            {!isLocked ? (
                                              <button
                                                className="p-2 rounded text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                title="Set PIN lock"
                                                onClick={() =>
                                                  handleSetCategoryPin(
                                                    categoryId
                                                  )
                                                }
                                              >
                                                <i className="fas fa-lock-open text-sm" />
                                              </button>
                                            ) : (
                                              <button
                                                className="p-2 rounded text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                title="Unlock category"
                                                onClick={() =>
                                                  handleUnlockCategory(
                                                    categoryId
                                                  )
                                                }
                                              >
                                                <i className="fas fa-lock text-sm" />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* Category Channels */}
                                      {isExpanded && (
                                        <div className="p-2">
                                          {isLoading ? (
                                            <div className="text-center py-4">
                                              <LoadingSpinner size="md" />
                                              <p className="text-gray-500 mt-2">
                                                Loading channels...
                                              </p>
                                            </div>
                                          ) : (
                                            <>
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {channels.map((channel) => {
                                                  const isFav = isFavorite(
                                                    channel.mediaId
                                                  );

                                                  return (
                                                    <div
                                                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                                                      key={channel.mediaId}
                                                    >
                                                      <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-white dark:bg-gray-800/30 rounded-lg shadow-sm flex items-center justify-center">
                                                          {channel.logoPath ||
                                                          channel.posterPath ? (
                                                            <img
                                                              alt={channel.name}
                                                              className="w-8 h-8 rounded object-contain"
                                                              src={
                                                                channel.logoPath ||
                                                                channel.posterPath
                                                              }
                                                            />
                                                          ) : (
                                                            <i className="fas fa-tv text-gray-500" />
                                                          )}
                                                        </div>
                                                        <div className="flex-1">
                                                          <p className="font-medium flex items-center gap-2">
                                                            {channel.name}
                                                            {isFav && (
                                                              <i className="fas fa-star text-yellow-500 text-xs" />
                                                            )}
                                                          </p>
                                                          <p className="text-xs text-gray-500">
                                                            {categoryName}
                                                          </p>
                                                        </div>
                                                      </div>
                                                      <div className="flex items-center gap-1">
                                                        <button
                                                          className={`p-1.5 rounded transition-all transform ${
                                                            processingFavorites.has(
                                                              channel.mediaId
                                                            )
                                                              ? "opacity-50 cursor-not-allowed"
                                                              : isFav
                                                                ? "bg-yellow-100 dark:bg-yellow-900/30 scale-110"
                                                                : "hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:scale-105"
                                                          }`}
                                                          disabled={processingFavorites.has(
                                                            channel.mediaId
                                                          )}
                                                          title={
                                                            isFav
                                                              ? "Remove from favorites"
                                                              : "Add to favorites"
                                                          }
                                                          onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleToggleFavorite(
                                                              channel
                                                            );
                                                          }}
                                                        >
                                                          {processingFavorites.has(
                                                            channel.mediaId
                                                          ) ? (
                                                            <i className="fas fa-spinner fa-spin text-xs" />
                                                          ) : (
                                                            <i
                                                              className={`fa-star ${
                                                                isFav
                                                                  ? "fas text-yellow-500 text-sm drop-shadow"
                                                                  : "far text-gray-400 text-xs"
                                                              } ${!isFav ? "hover:text-yellow-400" : ""}`}
                                                            />
                                                          )}
                                                        </button>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                              {/* Pagination */}
                                              {pagination &&
                                                pagination.totalPages > 1 && (
                                                  <div className="mt-4 flex justify-center">
                                                    <button
                                                      className="btn btn-sm"
                                                      disabled={
                                                        pagination.currentPage >=
                                                        pagination.totalPages
                                                      }
                                                      onClick={() =>
                                                        loadMoreChannels(
                                                          categoryId
                                                        )
                                                      }
                                                    >
                                                      {loadingChannels[
                                                        categoryId
                                                      ] ? (
                                                        <>
                                                          <LoadingSpinner size="sm" />
                                                          <span className="ml-2">
                                                            Loading...
                                                          </span>
                                                        </>
                                                      ) : (
                                                        <>
                                                          Load More (
                                                          {channels.length} of{" "}
                                                          {
                                                            pagination.totalItems
                                                          }
                                                          )
                                                        </>
                                                      )}
                                                    </button>
                                                  </div>
                                                )}
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </StrictModeDroppable>
                    </DragDropContext>
                  </div>
                ) : (
                  <div className="card p-8">
                    <div className="text-center">
                      <i className="fas fa-tv text-4xl text-gray-300 mb-3" />
                      <p className="text-gray-500">
                        No channel categories available
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "movies" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: 20 }}
              key="movies"
            >
              <div className="space-y-4">
                {/* Header Stats */}
                <div className="card p-4 shadow-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold">Movie Library</h3>
                      <div className="flex gap-4 text-sm text-gray-500 mt-1">
                        <span>
                          <i className="fas fa-film mr-1" /> Total Movies:{" "}
                          {(
                            movieCategories.find(
                              (cat) => cat.categoryName === "All"
                            )?.itemCount || 0
                          ).toLocaleString()}
                        </span>
                        <span>
                          <i className="fas fa-folder mr-1" /> Categories:{" "}
                          {movieCategories.length}
                        </span>
                        <span>
                          <i className="fas fa-eye-slash mr-1" /> Hidden:{" "}
                          {hiddenCategories.size}
                        </span>
                        <span>
                          <i className="fas fa-star mr-1" /> Favorites:{" "}
                          {favorites.length}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          className="input pl-10 pr-4 py-2 text-sm w-64"
                          placeholder="Search movies..."
                          type="text"
                          value={movieSearchQuery}
                          onChange={(e) =>
                            handleMovieSearchChange(e.target.value)
                          }
                        />
                        <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        {movieSearchQuery && (
                          <button
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setMovieSearchQuery("");
                              setMovieSearchResults([]);
                              setMovieSearchPagination(null);
                            }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>
                      {hiddenCategories.size > 0 && (
                        <button
                          className={`btn btn-sm ${showHiddenCategories ? "btn-warning" : "btn-ghost"}`}
                          onClick={() =>
                            setShowHiddenCategories(!showHiddenCategories)
                          }
                        >
                          <i className="fas fa-eye mr-2" />
                          {showHiddenCategories ? "Hide" : "Show"} Hidden (
                          {hiddenCategories.size})
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Categorized Movies */}
                {loadingMovieCategories ? (
                  <div className="card p-8 shadow-md">
                    <div className="text-center">
                      <LoadingSpinner size="lg" />
                      <p className="text-gray-500 mt-3">
                        Loading movie categories...
                      </p>
                    </div>
                  </div>
                ) : movieSearchQuery ? (
                  // Show search results when searching
                  <div className="space-y-3">
                    {movieSearchLoading ? (
                      <div className="card p-8 shadow-md">
                        <div className="text-center">
                          <LoadingSpinner size="lg" />
                          <p className="text-gray-500 mt-3">
                            Searching for "{movieSearchQuery}"...
                          </p>
                        </div>
                      </div>
                    ) : movieSearchResults.length > 0 ? (
                      <div className="card p-4 shadow-md">
                        <div className="mb-4">
                          <h4 className="font-semibold text-lg">
                            Search Results ({movieSearchResults.length} movies)
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {movieSearchResults.map((movie) => {
                            const isFav = isFavorite(movie.mediaId);
                            const category = movieCategories.find(
                              (cat) => cat.categoryId === movie.categoryId
                            );

                            return (
                              <div
                                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                                key={movie.mediaId}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-white dark:bg-gray-800/30 rounded-lg shadow-sm flex items-center justify-center">
                                    {movie.logoPath || movie.posterPath ? (
                                      <img
                                        alt={movie.name}
                                        className="w-8 h-8 rounded object-contain"
                                        src={movie.logoPath || movie.posterPath}
                                      />
                                    ) : (
                                      <i className="fas fa-film text-gray-500" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium flex items-center gap-2">
                                      {movie.name}
                                      {isFav && (
                                        <i className="fas fa-star text-yellow-500 text-xs" />
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                        {category
                                          ? category.categoryName
                                          : "Movie"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    className={`p-1.5 rounded transition-all transform ${
                                      processingFavorites.has(movie.mediaId)
                                        ? "opacity-50 cursor-not-allowed"
                                        : isFav
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 scale-110"
                                          : "hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:scale-105"
                                    }`}
                                    disabled={processingFavorites.has(
                                      movie.mediaId
                                    )}
                                    title={
                                      isFav
                                        ? "Remove from favorites"
                                        : "Add to favorites"
                                    }
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleFavorite(movie);
                                    }}
                                  >
                                    {processingFavorites.has(movie.mediaId) ? (
                                      <LoadingSpinner size="xs" />
                                    ) : (
                                      <i
                                        className={`${isFav ? "fas" : "far"} fa-star ${
                                          isFav
                                            ? "text-yellow-500"
                                            : "text-gray-400"
                                        }`}
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Pagination for search results */}
                        {movieSearchPagination &&
                          movieSearchPagination.totalPages > 1 &&
                          movieSearchPagination.currentPage <
                            movieSearchPagination.totalPages && (
                            <div className="mt-4 flex justify-center">
                              <button
                                className="btn btn-sm"
                                disabled={movieSearchLoading}
                                onClick={loadMoreMovieSearchResults}
                              >
                                {movieSearchLoading ? (
                                  <>
                                    <LoadingSpinner size="sm" />
                                    <span className="ml-2">Loading...</span>
                                  </>
                                ) : (
                                  <>
                                    Load More ({movieSearchResults.length} of{" "}
                                    {movieSearchPagination.totalItems})
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="card p-8 shadow-md">
                        <div className="text-center">
                          <i className="fas fa-search text-4xl text-gray-300 mb-3" />
                          <p className="text-gray-500">
                            No movies found matching "{movieSearchQuery}"
                          </p>
                          <button
                            className="btn btn-ghost btn-sm mt-4"
                            onClick={() => {
                              setMovieSearchQuery("");
                              setMovieSearchResults([]);
                              setMovieSearchPagination(null);
                            }}
                          >
                            Clear Search
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : movieCategories.length > 0 ? (
                  // Show categories as compact buttons
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                    {movieCategories.map((category) => {
                      const { uniqueId, categoryName, itemCount } = category;
                      const isHidden = hiddenCategories.has(
                        category.originalCategoryId
                      );

                      // Skip hidden categories unless we're showing them
                      if (isHidden && !showHiddenCategories) return null;

                      return (
                        <button
                          className="card p-3 text-left hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20"
                          key={uniqueId}
                          onClick={() => {
                            // Navigate to category view with categoryId
                            navigate(
                              `/playlist-manager/${playlistId}/manage/movies/${category.originalCategoryId}/${encodeURIComponent(categoryName)}`
                            );
                          }}
                        >
                          <div className="font-semibold text-sm line-clamp-1 mb-1">
                            {categoryName}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {itemCount ? itemCount.toLocaleString() : 0}
                          </div>
                          {isHidden && (
                            <span className="badge badge-warning badge-xs mt-1">
                              Hidden
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="card p-8">
                    <div className="text-center">
                      <i className="fas fa-film text-4xl text-gray-300 mb-3" />
                      <p className="text-gray-500">
                        No movie categories available
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "series" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: 20 }}
              key="series"
            >
              <div className="space-y-4">
                {/* Header Stats */}
                <div className="card p-4 shadow-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold">Series Library</h3>
                      <div className="flex gap-4 text-sm text-gray-500 mt-1">
                        <span>
                          <i className="fas fa-video mr-1" /> Total Series:{" "}
                          {(
                            seriesCategories.find(
                              (cat) => cat.categoryName === "All"
                            )?.itemCount || 0
                          ).toLocaleString()}
                        </span>
                        <span>
                          <i className="fas fa-folder mr-1" /> Categories:{" "}
                          {seriesCategories.length}
                        </span>
                        <span>
                          <i className="fas fa-eye-slash mr-1" /> Hidden:{" "}
                          {hiddenCategories.size}
                        </span>
                        <span>
                          <i className="fas fa-star mr-1" /> Favorites:{" "}
                          {favorites.length}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          className="input pl-10 pr-4 py-2 text-sm w-64"
                          placeholder="Search series..."
                          type="text"
                          value={seriesSearchQuery}
                          onChange={(e) =>
                            handleSeriesSearchChange(e.target.value)
                          }
                        />
                        <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        {seriesSearchQuery && (
                          <button
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                              setSeriesSearchQuery("");
                              setSeriesSearchResults([]);
                              setSeriesSearchPagination(null);
                            }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>
                      {hiddenCategories.size > 0 && (
                        <button
                          className={`btn btn-sm ${showHiddenCategories ? "btn-warning" : "btn-ghost"}`}
                          onClick={() =>
                            setShowHiddenCategories(!showHiddenCategories)
                          }
                        >
                          <i className="fas fa-eye mr-2" />
                          {showHiddenCategories ? "Hide" : "Show"} Hidden (
                          {hiddenCategories.size})
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Categorized Series */}
                {loadingSeriesCategories ? (
                  <div className="card p-8 shadow-md">
                    <div className="text-center">
                      <LoadingSpinner size="lg" />
                      <p className="text-gray-500 mt-3">
                        Loading series categories...
                      </p>
                    </div>
                  </div>
                ) : seriesSearchQuery ? (
                  // Show search results when searching
                  <div className="space-y-3">
                    {seriesSearchLoading ? (
                      <div className="card p-8 shadow-md">
                        <div className="text-center">
                          <LoadingSpinner size="lg" />
                          <p className="text-gray-500 mt-3">
                            Searching for "{seriesSearchQuery}"...
                          </p>
                        </div>
                      </div>
                    ) : seriesSearchResults.length > 0 ? (
                      <div className="card p-4 shadow-md">
                        <div className="mb-4">
                          <h4 className="font-semibold text-lg">
                            Search Results ({seriesSearchResults.length} series)
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {seriesSearchResults.map((series) => {
                            const isFav = isFavorite(series.mediaId);
                            const category = seriesCategories.find(
                              (cat) => cat.categoryId === series.categoryId
                            );

                            return (
                              <div
                                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                                key={series.mediaId}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-white dark:bg-gray-800/30 rounded-lg shadow-sm flex items-center justify-center">
                                    {series.logoPath || series.posterPath ? (
                                      <img
                                        alt={series.name}
                                        className="w-8 h-8 rounded object-contain"
                                        src={
                                          series.logoPath || series.posterPath
                                        }
                                      />
                                    ) : (
                                      <i className="fas fa-video text-gray-500" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium flex items-center gap-2">
                                      {series.name}
                                      {isFav && (
                                        <i className="fas fa-star text-yellow-500 text-xs" />
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                                        {category
                                          ? category.categoryName
                                          : "Series"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    className={`p-1.5 rounded transition-all transform ${
                                      processingFavorites.has(series.mediaId)
                                        ? "opacity-50 cursor-not-allowed"
                                        : isFav
                                          ? "bg-yellow-100 dark:bg-yellow-900/30 scale-110"
                                          : "hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:scale-105"
                                    }`}
                                    disabled={processingFavorites.has(
                                      series.mediaId
                                    )}
                                    title={
                                      isFav
                                        ? "Remove from favorites"
                                        : "Add to favorites"
                                    }
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggleFavorite(series);
                                    }}
                                  >
                                    {processingFavorites.has(series.mediaId) ? (
                                      <LoadingSpinner size="xs" />
                                    ) : (
                                      <i
                                        className={`${isFav ? "fas" : "far"} fa-star ${
                                          isFav
                                            ? "text-yellow-500"
                                            : "text-gray-400"
                                        }`}
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Pagination for search results */}
                        {seriesSearchPagination &&
                          seriesSearchPagination.totalPages > 1 &&
                          seriesSearchPagination.currentPage <
                            seriesSearchPagination.totalPages && (
                            <div className="mt-4 flex justify-center">
                              <button
                                className="btn btn-sm"
                                disabled={seriesSearchLoading}
                                onClick={loadMoreSeriesSearchResults}
                              >
                                {seriesSearchLoading ? (
                                  <>
                                    <LoadingSpinner size="sm" />
                                    <span className="ml-2">Loading...</span>
                                  </>
                                ) : (
                                  <>
                                    Load More ({seriesSearchResults.length} of{" "}
                                    {seriesSearchPagination.totalItems})
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="card p-8 shadow-md">
                        <div className="text-center">
                          <i className="fas fa-search text-4xl text-gray-300 mb-3" />
                          <p className="text-gray-500">
                            No series found matching "{seriesSearchQuery}"
                          </p>
                          <button
                            className="btn btn-ghost btn-sm mt-4"
                            onClick={() => {
                              setSeriesSearchQuery("");
                              setSeriesSearchResults([]);
                              setSeriesSearchPagination(null);
                            }}
                          >
                            Clear Search
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : seriesCategories.length > 0 ? (
                  // Show categories as compact buttons
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                    {seriesCategories.map((category) => {
                      const { uniqueId, categoryName, itemCount } = category;
                      const isHidden = hiddenCategories.has(
                        category.originalCategoryId
                      );

                      // Skip hidden categories unless we're showing them
                      if (isHidden && !showHiddenCategories) return null;

                      return (
                        <button
                          className="card p-3 text-left hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20"
                          key={uniqueId}
                          onClick={() => {
                            // Navigate to category view with categoryId
                            navigate(
                              `/playlist-manager/${playlistId}/manage/series/${category.originalCategoryId}/${encodeURIComponent(categoryName)}`
                            );
                          }}
                        >
                          <div className="font-semibold text-sm line-clamp-1 mb-1">
                            {categoryName}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {itemCount ? itemCount.toLocaleString() : 0}
                          </div>
                          {isHidden && (
                            <span className="badge badge-warning badge-xs mt-1">
                              Hidden
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="card p-8">
                    <div className="text-center">
                      <i className="fas fa-video text-4xl text-gray-300 mb-3" />
                      <p className="text-gray-500">
                        No series categories available
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "favorites" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: 20 }}
              key="favorites"
            >
              <div
                className="card p-6 shadow-md"
                style={{
                  backgroundImage: "url(/favorites-background.png)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base sm:text-lg font-semibold">
                    <span className="inline-block w-[10px]" />
                    Favorite Channels <span className="inline-block w-[10px]" />
                    ({favorites.length})
                  </h3>
                  {favorites.length > 0 && (
                    <button
                      className="btn btn-sm btn-ghost text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={handleRemoveAllFavorites}
                    >
                      <i className="fas fa-trash-alt mr-1" />
                      Remove All
                    </button>
                  )}
                </div>

                {favorites.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {favorites.map((channel) => (
                      <div
                        className="p-4 bg-gray-700/40 backdrop-blur-sm border border-gray-600/50 rounded-lg hover:bg-gray-700/50 transition-colors"
                        key={channel.id}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-12 h-12 bg-white dark:bg-gray-800/30 rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
                              {channel.logo ? (
                                <img
                                  alt={channel.name}
                                  className="w-10 h-10 rounded object-contain"
                                  src={channel.logo}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className={`${channel.logo ? "hidden" : "flex"} w-full h-full items-center justify-center`}
                                style={{
                                  display: channel.logo ? "none" : "flex",
                                }}
                              >
                                <i className="fas fa-tv text-gray-400" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium">{channel.name}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {channel.group ||
                                  channel.category ||
                                  "Uncategorized"}
                              </p>
                            </div>
                          </div>
                          <button
                            className="text-red-500 hover:text-red-600 ml-2"
                            title="Remove from favorites"
                            onClick={() => handleRemoveFavorite(channel)}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <i className="fas fa-star text-4xl text-gray-300 mb-3" />
                    <p className="text-gray-500">No favorites yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Add channels to favorites from the Channels tab
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === "settings" && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: 20 }}
              key="settings"
            >
              <div className="card p-6 shadow-md">
                <h3 className="text-base sm:text-lg font-semibold mb-6 pt-[10px]">
                  <span className="inline-block w-[10px]" />
                  Player Settings
                </h3>

                <div className="space-y-6">
                  {/* Decoder Settings */}
                  <div>
                    <h4 className="font-medium mb-3">Decoder</h4>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <span className="font-medium">
                            Use Hardware Decoder
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            Better performance, uses device hardware
                          </p>
                        </div>
                        <div className="toggle-switch">
                          <input
                            checked={settings.useHardwareDecoder}
                            type="checkbox"
                            onChange={() =>
                              handleSettingToggle("useHardwareDecoder")
                            }
                          />
                          <span className="toggle-bg" />
                          <span className="toggle-handle" />
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Display Settings */}
                  <div>
                    <h4 className="font-medium mb-3">Display</h4>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <span className="font-medium">Closed Captions</span>
                          <p className="text-xs text-gray-500 mt-1">
                            Show subtitles when available
                          </p>
                        </div>
                        <div className="toggle-switch">
                          <input
                            checked={settings.closedCaptions}
                            type="checkbox"
                            onChange={() =>
                              handleSettingToggle("closedCaptions")
                            }
                          />
                          <span className="toggle-bg" />
                          <span className="toggle-handle" />
                        </div>
                      </label>

                      <label className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <span className="font-medium">Mute Miniplayer</span>
                          <p className="text-xs text-gray-500 mt-1">
                            Mute audio in picture-in-picture mode
                          </p>
                        </div>
                        <div className="toggle-switch">
                          <input
                            checked={settings.muteMiniplayer}
                            type="checkbox"
                            onChange={() =>
                              handleSettingToggle("muteMiniplayer")
                            }
                          />
                          <span className="toggle-bg" />
                          <span className="toggle-handle" />
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* System Settings */}
                  <div>
                    <h4 className="font-medium mb-3">System</h4>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <span className="font-medium">Start at Launch</span>
                          <p className="text-xs text-gray-500 mt-1">
                            Automatically start when device boots
                          </p>
                        </div>
                        <div className="toggle-switch">
                          <input
                            checked={settings.startAtLaunch}
                            type="checkbox"
                            onChange={() =>
                              handleSettingToggle("startAtLaunch")
                            }
                          />
                          <span className="toggle-bg" />
                          <span className="toggle-handle" />
                        </div>
                      </label>

                      <label className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <span className="font-medium">
                            Clear Cache Automatically
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            Clear cache when app closes
                          </p>
                        </div>
                        <div className="toggle-switch">
                          <input
                            checked={settings.clearCacheAuto}
                            type="checkbox"
                            onChange={() =>
                              handleSettingToggle("clearCacheAuto")
                            }
                          />
                          <span className="toggle-bg" />
                          <span className="toggle-handle" />
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Data Management */}
                  <div>
                    <h4 className="font-medium mb-3">Data Management</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <button
                        className="btn btn-outline"
                        onClick={handleClearCache}
                      >
                        <i className="fas fa-broom mr-2" />
                        Clear Cache
                      </button>
                      <button
                        className="btn btn-outline"
                        onClick={handleClearRecentlyWatched}
                      >
                        <i className="fas fa-history mr-2" />
                        Clear History
                      </button>
                      <button
                        className="btn btn-outline text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                        onClick={handleClearData}
                      >
                        <i className="fas fa-trash-alt mr-2" />
                        Clear All Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* PIN Modal */}
        <AnimatePresence>
          {showPinModal &&
            (pinAction === "reset" || pinAction === "setAndLock") && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowPinModal(false);
                    setNewPin("");
                    setConfirmNewPin("");
                    setShowNewPin(false);
                    setShowConfirmPin(false);
                    setCurrentPinCategory(null);
                    setPinAction(null);
                  }
                }}
              >
                <motion.div
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white dark:bg-gray-800/90 rounded-xl p-6 w-full max-w-sm"
                  exit={{ opacity: 0, scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-bold mb-4">
                    {pinAction === "setAndLock"
                      ? "Set PIN Code"
                      : "Reset PIN Code"}
                  </h3>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {pinAction === "setAndLock"
                        ? `Set a PIN to lock "${categories.find((cat) => cat.categoryId === currentPinCategory)?.categoryName || "category"}"`
                        : "Change your PIN code to control access to locked categories."}
                    </p>
                  </div>

                  <div className="mb-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        New PIN
                      </label>
                      <div className="relative">
                        <input
                          autoFocus
                          className="input w-full text-center text-2xl font-mono tracking-widest pr-10"
                          maxLength="4"
                          placeholder="••••"
                          type={showNewPin ? "text" : "password"}
                          value={newPin}
                          onChange={(e) =>
                            setNewPin(
                              e.target.value.replace(/\D/g, "").slice(0, 4)
                            )
                          }
                        />
                        <button
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          type="button"
                          onClick={() => setShowNewPin(!showNewPin)}
                        >
                          <i
                            className={`fas fa-eye${showNewPin ? "" : "-slash"}`}
                          />
                        </button>
                      </div>
                      <p className="text-xs text-center text-gray-500 mt-1">
                        {newPin.length}/4 digits
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Confirm New PIN
                      </label>
                      <div className="relative">
                        <input
                          className="input w-full text-center text-2xl font-mono tracking-widest pr-10"
                          maxLength="4"
                          placeholder="••••"
                          type={showConfirmPin ? "text" : "password"}
                          value={confirmNewPin}
                          onChange={(e) =>
                            setConfirmNewPin(
                              e.target.value.replace(/\D/g, "").slice(0, 4)
                            )
                          }
                        />
                        <button
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          type="button"
                          onClick={() => setShowConfirmPin(!showConfirmPin)}
                        >
                          <i
                            className={`fas fa-eye${showConfirmPin ? "" : "-slash"}`}
                          />
                        </button>
                      </div>
                      <p className="text-xs text-center text-gray-500 mt-1">
                        {confirmNewPin.length}/4 digits
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      className="btn btn-ghost flex-1 justify-center cursor-pointer"
                      onClick={() => {
                        setShowPinModal(false);
                        setNewPin("");
                        setConfirmNewPin("");
                        setShowNewPin(false);
                        setShowConfirmPin(false);
                        setCurrentPinCategory(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn bg-red-500 text-white hover:bg-red-600 active:bg-red-700 shadow-sm hover:shadow-md rounded-full font-semibold flex-1 justify-center cursor-pointer"
                      onClick={handleRemovePin}
                    >
                      Remove
                    </button>
                    <button
                      className="btn btn-primary flex-1 justify-center cursor-pointer"
                      disabled={
                        newPin.length !== 4 || confirmNewPin.length !== 4
                      }
                      onClick={handleVerifyPin}
                    >
                      {pinAction === "setAndLock" ? "Set PIN & Lock" : "Update"}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
        </AnimatePresence>

        {/* Avatar Picker Modal */}
        <AnimatePresence>
          {showAvatarPicker && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowAvatarPicker(false);
                }
              }}
            >
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-gray-800/90 rounded-xl p-6 w-full max-w-md"
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold mb-4">Choose Avatar</h3>

                <div className="grid grid-cols-4 gap-4 mb-6 max-h-96 overflow-y-auto">
                  {availableAvatars.map((avatar) => (
                    <button
                      className={`w-20 h-20 rounded-full overflow-hidden hover:ring-4 hover:ring-primary-500 transition-all relative ${
                        profile.avatar === avatar.url
                          ? "ring-4 ring-blue-500"
                          : ""
                      }`}
                      key={avatar.id}
                      onClick={() => handleAvatarSelect(avatar)}
                    >
                      <img
                        alt={`Avatar ${avatar.id}`}
                        className="w-full h-full"
                        src={avatar.url}
                      />
                      {profile.avatar === avatar.url && (
                        <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                          <div className="bg-white rounded-full p-1">
                            <svg
                              className="w-6 h-6 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                clipRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                fillRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                <button
                  className="w-full btn btn-ghost"
                  onClick={() => setShowAvatarPicker(false)}
                >
                  Cancel
                </button>
              </motion.div>
            </div>
          )}

          {/* Message Modal */}
          {showMessageModal && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowMessageModal(false);
                  setMessageContent("");
                }
              }}
            >
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="card w-full max-w-lg p-6 shadow-md"
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">
                    Send Message to {profile.name || user?.name || "User"}
                  </h3>
                  <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setShowMessageModal(false)}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      autoFocus
                      className="input w-full resize-none"
                      placeholder="Type your message here..."
                      rows={8}
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        {messageContent.length} characters
                      </p>
                      <button
                        className="text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => setMessageContent("")}
                      >
                        <i className="fas fa-eraser mr-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setShowMessageModal(false);
                      setMessageContent("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!messageContent.trim()}
                    onClick={handleSendMessage}
                  >
                    <i className="fas fa-paper-plane mr-2" />
                    Send Message
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Remove All Favorites Modal */}
          {showRemoveAllModal && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={(e) => {
                if (e.target === e.currentTarget && !removingAllFavorites) {
                  setShowRemoveAllModal(false);
                }
              }}
            >
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="card w-full max-w-md p-6 shadow-md"
                exit={{ opacity: 0, scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">
                    Remove All Favorites
                  </h3>
                  {!removingAllFavorites && (
                    <button
                      className="text-gray-400 hover:text-gray-200"
                      onClick={() => setShowRemoveAllModal(false)}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {!removingAllFavorites ? (
                    <>
                      <p className="text-gray-300">
                        Are you sure you want to remove all {favorites.length}{" "}
                        favorite{favorites.length !== 1 ? "s" : ""}?
                      </p>
                      <p className="text-sm text-gray-400">
                        This action will remove all your bookmarked channels,
                        movies, and shows.
                      </p>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-gray-300">Removing favorites...</p>
                      <div className="relative">
                        <div className="flex justify-between text-sm text-gray-400 mb-1">
                          <span>Progress</span>
                          <span>
                            {removalProgress.current} / {removalProgress.total}
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${
                                removalProgress.total > 0
                                  ? (removalProgress.current /
                                      removalProgress.total) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  {!removingAllFavorites ? (
                    <>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setShowRemoveAllModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn bg-red-600 hover:bg-red-700 text-white"
                        onClick={confirmRemoveAllFavorites}
                      >
                        <i className="fas fa-trash-alt mr-2" />
                        Remove All
                      </button>
                    </>
                  ) : (
                    <button disabled className="btn btn-ghost">
                      <i className="fas fa-spinner fa-spin mr-2" />
                      Removing...
                    </button>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PlaylistManager;
